import {
    auditLogSearchSchema,
    defaultAuditLogFrom,
    draftToAuditLogSearch,
    parseCsv,
    searchToAuditFiltersDraft,
    serializeAuditLogSearch,
} from "@/features/audit/utils/auditFilterSearch";
import { isoToDraftDateTime } from "@/features/audit/utils/auditDateTimeFilter";
import { AuditFiltersPanel, type AuditFiltersDraft } from "@/features/audit/components/AuditFiltersPanel";
import { isAuditorView } from "@/features/audit/api/auditorApi";
import { AuditLogNavTabs } from "@/features/audit/components/AuditLogNavTabs";
import { useAuditEnabledTables } from "@/features/audit/hooks/useAuditEnabledTables";
import {
    createEmptyAuditFiltersDraft,
    draftScopeModeChange,
    useAuditDraftScopePreview,
} from "@/features/audit/hooks/useAuditDraftScopePreview";
import { useAuditLogSharedState } from "@/features/audit/hooks/useAuditLogSharedState";
import { AppPageHeader } from "@/components/common/AppPageHeader";
import { AppPageLayout } from "@/components/common/AppPageLayout";
import { requireTab } from "@/features/navigation/requireTab";
import { useNav } from "@/utils/nav";
import { Button } from "@patient-watch/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@patient-watch/ui/sheet";
import { Outlet, createFileRoute, useRouterState } from "@tanstack/react-router";
import { SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/_app/audit")({
    validateSearch: (search) => auditLogSearchSchema.parse(search),
    beforeLoad: ({ context }) => requireTab(context, "audit"),
    component: AuditLogLayout,
});

function AuditLogLayout() {
    useNav([{ label: "Audit", route: { to: "/audit/change-timeline" } }]);

    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const { scopeSummary, isResolvingScope } = useAuditLogSharedState();
    const pathname = useRouterState({ select: (state) => state.location.pathname });
    // Snapshot and lock-diaries have their own diary pickers; the audit filter sheet
    // (and its default changedFrom seeding) only drives the change timeline.
    const onTimelinePage = pathname.includes("/change-timeline");

    const defaultFromIso = useMemo(() => defaultAuditLogFrom(), []);
    const defaultFromLocal = useMemo(() => isoToDraftDateTime(defaultFromIso), [defaultFromIso]);
    const hasSeededDefaultFrom = useRef(false);

    const [draft, setDraft] = useState<AuditFiltersDraft>(() => searchToAuditFiltersDraft(search, defaultFromIso));
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const [isApplying, setIsApplying] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    useEffect(() => {
        setDraft(searchToAuditFiltersDraft(search, defaultFromIso));
    }, [search, defaultFromIso]);

    useEffect(() => {
        if (!onTimelinePage || search.changedFrom || hasSeededDefaultFrom.current) return;

        hasSeededDefaultFrom.current = true;
        void navigate({
            to: pathname,
            search: () =>
                serializeAuditLogSearch({
                    ...search,
                    changedFrom: defaultFromIso,
                }),
            replace: true,
        });
    }, [search, defaultFromIso, navigate, pathname, onTimelinePage]);

    const { data: tableOptions = [] } = useAuditEnabledTables();
    const { draftPreview } = useAuditDraftScopePreview(draft);

    const hasActiveFilters = useMemo(() => {
        return (
            Boolean(parseCsv(search.labels).length) ||
            Boolean(parseCsv(search.diaries).length) ||
            Boolean(parseCsv(search.orgs).length) ||
            Boolean(parseCsv(search.table).length) ||
            Boolean(search.changedFrom && search.changedFrom !== defaultFromIso) ||
            Boolean(search.changedTo) ||
            Boolean(search.asOf)
        );
    }, [search, defaultFromIso]);

    const applyFilters = () => {
        setIsApplying(true);
        const nextSearch = draftToAuditLogSearch(draftRef.current, search, defaultFromIso);

        void navigate({
            to: pathname,
            search: () => nextSearch,
            replace: true,
        }).finally(() => {
            setIsApplying(false);
            setFiltersOpen(false);
        });
    };

    const activeFilterCount =
        parseCsv(search.labels).length +
        parseCsv(search.diaries).length +
        parseCsv(search.orgs).length +
        parseCsv(search.table).length +
        (search.changedFrom && search.changedFrom !== defaultFromIso ? 1 : 0) +
        (search.changedTo ? 1 : 0) +
        (search.asOf ? 1 : 0);

    const clearFilters = () => {
        setDraft(createEmptyAuditFiltersDraft(defaultFromLocal));
        void navigate({
            to: pathname,
            search: () =>
                serializeAuditLogSearch({
                    scope: "labels",
                    changedFrom: defaultFromIso,
                }),
            replace: true,
        });
    };

    return (
        <AppPageLayout width="full" className="space-y-6">
            <AppPageHeader
                title="Audit"
                description="Change history, point-in-time clinical snapshots, and diary sign-off."
            />
            {isAuditorView() && (
                <p className="text-muted-foreground text-xs" data-testid="audit-hashed-note">
                    Patient details are hidden: diaries are shown by id, answers as [answer redacted], and names and
                    contact details as codes such as #3f2a9c1b04de. The same value always gives the same code.
                </p>
            )}
            <div className="space-y-4">
                <div className="flex justify-between items-end gap-2">
                    <AuditLogNavTabs search={search} />

                    {onTimelinePage && (
                        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                            <SheetTrigger asChild>
                                <Button variant="outline" size="sm" type="button" className="shrink-0">
                                    <SlidersHorizontal className="w-4 h-4" />
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <span className="flex justify-center items-center bg-indigo-100 ml-1 px-1 rounded-full min-w-[18px] h-[18px] font-medium text-indigo-700 text-xs leading-none">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </Button>
                            </SheetTrigger>
                            <SheetContent className="w-full sm:max-w-md overflow-y-auto">
                                <SheetHeader className="mb-2">
                                    <SheetTitle>Audit filters</SheetTitle>
                                    <SheetDescription>
                                        Scope filters apply to both views; timeline filters only affect the change
                                        timeline.
                                    </SheetDescription>
                                </SheetHeader>
                                <AuditFiltersPanel
                                    draft={draft}
                                    onScopeModeChange={(mode) =>
                                        setDraft((current) => draftScopeModeChange(current, mode))
                                    }
                                    onLabelsChange={(labels) => setDraft((current) => ({ ...current, labels }))}
                                    onDiaryIdsChange={(diaryIds) => setDraft((current) => ({ ...current, diaryIds }))}
                                    onOrganisationIdsChange={(organisationIds) =>
                                        setDraft((current) => ({ ...current, organisationIds }))
                                    }
                                    onTablesChange={(tables) => setDraft((current) => ({ ...current, tables }))}
                                    onFromChange={(from) => setDraft((current) => ({ ...current, from }))}
                                    onToChange={(to) => setDraft((current) => ({ ...current, to }))}
                                    tableOptions={tableOptions}
                                    scopeSummary={scopeSummary}
                                    draftPreview={draftPreview}
                                    showTimelineFilters
                                    isApplying={isApplying}
                                    isBusy={isResolvingScope}
                                    hasActiveFilters={hasActiveFilters}
                                    onApply={applyFilters}
                                    onClear={clearFilters}
                                />
                            </SheetContent>
                        </Sheet>
                    )}
                </div>

                {onTimelinePage && scopeSummary && (
                    <p className="text-sm">
                        <span className="text-muted-foreground">Active scope: </span>
                        <span className="font-medium">{scopeSummary}</span>
                    </p>
                )}

                <Outlet />
            </div>
        </AppPageLayout>
    );
}
